import { ComponentType } from 'react';

import { capitalize } from 'lodash';

import { Flex, Tag, TagGroup, Text } from '@backstage/ui';

import {
  StatusOK,
  StatusPending,
  StatusRunning,
  StatusWarning,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import {
  FlagsAdminV1SegmentState,
  Maybe,
  isType,
} from '@spotify/confidence-graphql';
import { useSegment } from '@spotify/plugin-confidence-flags-react';
import { useSetResolvedTitle } from '@spotify/plugin-confidence-projects-react';

import { PreviewShell } from './components';

interface SegmentArtifactPreviewProps {
  resourceName: string;
  label: string;
}

const STATE_DOTS: Record<FlagsAdminV1SegmentState, ComponentType> = {
  [FlagsAdminV1SegmentState.Allocated]: StatusOK,
  [FlagsAdminV1SegmentState.Archived]: StatusPending,
  [FlagsAdminV1SegmentState.Unallocated]: StatusRunning,
  [FlagsAdminV1SegmentState.Ok]: StatusOK,
};

const StateIndicator = ({
  state,
}: {
  state?: Maybe<FlagsAdminV1SegmentState>;
}) => {
  const Dot = state ? STATE_DOTS[state] : StatusWarning;
  return (
    <Flex align="center" gap="2">
      <Dot />
      <Text variant="body-small" color="secondary">
        {capitalize(state ?? 'unknown')}
      </Text>
    </Flex>
  );
};

const formatAllocation = (proportion: unknown): string | undefined => {
  if (typeof proportion !== 'string' && typeof proportion !== 'number') {
    return undefined;
  }
  const value = Number(proportion);
  if (!Number.isFinite(value)) return undefined;
  return `${Math.round(value * 100)}%`;
};

export const SegmentArtifactPreview = ({
  resourceName,
  label,
}: SegmentArtifactPreviewProps) => {
  const { segment, isLoading, error } = useSegment({ name: resourceName });

  const linkedFlag = isType(segment?.flag, 'FlagsAdminV1Flag')
    ? segment.flag
    : undefined;
  const previewError =
    error ?? (!isLoading && !segment ? 'not-found' : undefined);

  useSetResolvedTitle(
    segment?.displayName || extractLastNameComponent(resourceName),
  );

  const allocation = formatAllocation(segment?.allocation?.proportion?.value);

  return (
    <PreviewShell
      resourceName={resourceName}
      label={label}
      loading={isLoading}
      error={previewError}
    >
      {segment && (
        <Flex direction="column" gap="2">
          <Flex direction="column" gap="1">
            <Text variant="body-medium" weight="bold">
              {segment.displayName ||
                extractLastNameComponent(segment.name) ||
                segment.name}
            </Text>
            <StateIndicator state={segment.state} />
          </Flex>
          {segment.description && (
            <Text variant="body-small">{segment.description}</Text>
          )}
          {allocation && (
            <Flex align="center" gap="2">
              <Text variant="body-small">Allocation:</Text>
              <Text variant="body-small" color="secondary">
                {allocation}
              </Text>
            </Flex>
          )}
          {linkedFlag &&
            (() => {
              const flagLabel =
                linkedFlag.displayName ||
                extractLastNameComponent(linkedFlag.name) ||
                linkedFlag.name;
              return (
                <Flex align="center" gap="2">
                  <Text variant="body-small">Flag:</Text>
                  <TagGroup aria-label="Flag">
                    <Tag
                      id={linkedFlag.name}
                      textValue={flagLabel}
                      size="small"
                    >
                      {flagLabel}
                    </Tag>
                  </TagGroup>
                </Flex>
              );
            })()}
        </Flex>
      )}
    </PreviewShell>
  );
};
